/**
 * Permission model (SRS §26).
 *
 * A permission code is "<module>.<entity>.<action>", e.g. "crm.customer.approve".
 * Actions: view, create, edit, submit, approve, post, deactivate, export, configure, delete.
 *
 * Roles hold a set of codes. Wildcards are allowed in role definitions:
 *   "*"                → everything (system administrator)
 *   "crm.*"            → every entity and action in CRM
 *   "crm.customer.*"   → every action on customers
 *   "*.*.view"         → read-only everywhere
 * Sensitive permissions (payroll data, supplier bank details) are NEVER matched
 * by wildcards — they must be granted explicitly.
 */

export const ACTIONS = ['view', 'create', 'edit', 'submit', 'approve', 'post', 'deactivate', 'export', 'configure', 'delete'] as const;
export type Action = (typeof ACTIONS)[number];

export type PermissionCode = `${string}.${string}.${Action}`;

export interface PermissionCatalogEntry {
  code: string;
  module: string;
  entity: string;
  action: Action;
  sensitive: boolean;
}

export function permissionCode(module: string, entity: string, action: Action): PermissionCode {
  return `${module}.${entity}.${action}`;
}

export function parsePermission(code: string): { module: string; entity: string; action: string } | null {
  const parts = code.split('.');
  if (parts.length !== 3 || parts.some((p) => p.length === 0)) return null;
  return { module: parts[0], entity: parts[1], action: parts[2] };
}

export class PermissionSet {
  private readonly exact = new Set<string>();
  private readonly patterns: Array<[string, string, string]> = [];
  private readonly sensitiveCodes: Set<string>;

  /**
   * @param granted    codes and wildcard patterns collected from the user's roles
   * @param catalog    optional catalogue, used to enforce the "no wildcard for sensitive" rule
   */
  constructor(granted: Iterable<string>, catalog: PermissionCatalogEntry[] = []) {
    this.sensitiveCodes = new Set(catalog.filter((c) => c.sensitive).map((c) => c.code));
    for (const g of granted) {
      const code = g.trim();
      if (!code) continue;
      if (code === '*') { this.patterns.push(['*', '*', '*']); continue; }
      const parts = code.split('.');
      if (parts.length === 1 && parts[0] !== '*') continue;       // malformed
      const [m = '*', e = '*', a = '*'] = parts;
      if (m.includes('*') || e.includes('*') || a.includes('*')) this.patterns.push([m, e, a]);
      else this.exact.add(code);
    }
  }

  has(code: string): boolean {
    if (this.exact.has(code)) return true;
    if (this.sensitiveCodes.has(code)) return false;              // wildcards never unlock sensitive data
    const p = parsePermission(code);
    if (!p) return false;
    return this.patterns.some(([m, e, a]) =>
      (m === '*' || m === p.module) && (e === '*' || e === p.entity) && (a === '*' || a === p.action));
  }

  can(module: string, entity: string, action: Action): boolean {
    return this.has(permissionCode(module, entity, action));
  }

  hasAny(codes: string[]): boolean { return codes.some((c) => this.has(c)); }
  hasAll(codes: string[]): boolean { return codes.every((c) => this.has(c)); }

  /** Which modules the user may see at all (drives the sidebar). */
  visibleModules(catalog: PermissionCatalogEntry[]): Set<string> {
    const out = new Set<string>();
    for (const c of catalog) if (c.action === 'view' && this.has(c.code)) out.add(c.module);
    return out;
  }
}

export class ForbiddenError extends Error {
  constructor(readonly permission: string, message = `Missing permission ${permission}`) {
    super(message);
    this.name = 'ForbiddenError';
  }
}

export function assertPermission(set: PermissionSet, code: string): void {
  if (!set.has(code)) throw new ForbiddenError(code);
}

/**
 * Default roles seeded for every tenant. They mirror the personas in the
 * mockups (General Manager, Sales Manager, …). Codes are stable identifiers;
 * names are translatable. Grants use wildcards, except sensitive data.
 */
/** document management as a manager uses it: file, read, link, submit and check in the documents of their area (confidentiality and access rules still gate each one); the document manager administers */
const DMS_USER = ['documents.document.view', 'documents.document.create', 'documents.document.edit', 'documents.document.submit', 'documents.document.export', 'documents.document_version.view', 'documents.document_link.*', 'documents.document_folder.view', 'documents.document_category.view', 'documents.document_template.view', 'documents.file.*'];
export const DEFAULT_ROLES: Array<{ code: string; name: string; nameAr: string; grants: string[] }> = [
  { code: 'system_administrator', name: 'System Administrator', nameAr: 'مدير النظام',
    grants: ['*', 'ai.assistant.configure', 'hr.employee_sensitive.view', 'hr.employee_sensitive.edit', 'procurement.supplier_bank_account.view', 'procurement.supplier_bank_account.create', 'procurement.supplier_bank_account.edit', 'procurement.supplier_bank_account.deactivate'] },
  { code: 'general_manager', name: 'General Manager', nameAr: 'المدير العام',
    grants: ['*.*.view', '*.*.approve', '*.*.export', 'contracts.*', 'crm.*', 'admin.approval.*', 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'sales_manager', name: 'Sales Manager', nameAr: 'مدير المبيعات',
    grants: ['crm.*.view', 'crm.*.create', 'crm.*.edit', 'crm.*.submit', 'crm.*.export', 'crm.*.deactivate',
             'crm.lead.approve', 'crm.opportunity.approve', 'crm.site_visit.approve', 'crm.estimation.approve', 'crm.quotation.approve', 'crm.sales_order.approve',
             'contracts.project.view', 'engineering.*.view', 'inventory.item.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'project_manager', name: 'Project Manager', nameAr: 'مدير المشروعات',
    grants: ['contracts.*', 'crm.customer.view', 'crm.contact.view', 'engineering.*.view', 'procurement.*.view', 'inventory.*.view', 'manufacturing.*.view', 'site.*', 'quality.*.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'engineering_manager', name: 'Engineering Manager', nameAr: 'مدير الهندسة',
    grants: ['engineering.*', 'contracts.project.view', 'contracts.project_node.*', 'inventory.item.view', 'inventory.uom.view', 'manufacturing.*.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'procurement_manager', name: 'Procurement Manager', nameAr: 'مدير المشتريات',
    grants: ['procurement.*', 'inventory.*.view', 'inventory.label.create', 'inventory.item.create', 'inventory.item.edit', 'finance.payment_term.view', 'finance.tax_code.view', 'finance.currency.view', ...DMS_USER,
             'procurement.supplier_bank_account.view', 'procurement.supplier_bank_account.create', 'procurement.supplier_bank_account.edit', 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'inventory_manager', name: 'Inventory Manager', nameAr: 'مدير المخازن',
    grants: ['inventory.*', 'procurement.goods_receipt.*', 'procurement.supplier.view', 'manufacturing.*.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'production_manager', name: 'Production Manager', nameAr: 'مدير الإنتاج',
    grants: ['manufacturing.*', 'engineering.*.view', 'inventory.*.view', 'inventory.material_issue.*', 'inventory.label.create', 'quality.*.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'quality_manager', name: 'Quality Manager', nameAr: 'مدير الجودة',
    grants: ['quality.*', 'manufacturing.*.view', 'procurement.goods_receipt.view', 'inventory.label.create', 'site.*.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'site_manager', name: 'Site & Installation Manager', nameAr: 'مدير المواقع والتركيبات',
    grants: ['site.*', 'contracts.project.view', 'inventory.*.view', 'inventory.material_issue.*', 'inventory.label.create', 'quality.*.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'finance_manager', name: 'Finance Manager', nameAr: 'المدير المالي',
    grants: ['finance.*', 'crm.customer.*', 'procurement.supplier.view', 'procurement.supplier_invoice.*', 'contracts.*.view', 'assets.*', 'admin.approval.*',
             'procurement.supplier_bank_account.view', 'hr.employee_sensitive.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'accountant', name: 'Accountant', nameAr: 'محاسب',
    grants: ['finance.*.view', 'finance.*.create', 'finance.*.edit', 'finance.*.submit', 'finance.*.export', 'crm.customer.view', 'procurement.supplier.view', 'contracts.*.view', 'documents.document.view', 'documents.document_version.view', 'documents.document_link.view', 'documents.document_folder.view', 'documents.document_category.view', 'documents.file.*', 'ai.assistant.view', 'ai.assistant.create'] },
  { code: 'hr_manager', name: 'HR Manager', nameAr: 'مدير الموارد البشرية',
    grants: ['hr.*', 'admin.department.*', 'admin.cost_center.view', 'hr.employee_sensitive.view', 'hr.employee_sensitive.edit', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'assets_manager', name: 'Assets Manager', nameAr: 'مدير الأصول',
    grants: ['assets.*', 'inventory.label.create', 'finance.gl_account.view', 'procurement.supplier.view', ...DMS_USER, 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'document_manager', name: 'Document Manager', nameAr: 'مدير الوثائق',
    grants: ['documents.*', '*.*.view', 'ai.assistant.view', 'ai.assistant.create', 'ai.insight.view'] },
  { code: 'employee', name: 'Employee (self-service)', nameAr: 'موظف',
    grants: ['hr.leave_request.view', 'hr.leave_request.create', 'hr.leave_request.submit', 'hr.employee_advance.view', 'hr.employee_advance.create', 'hr.employee_advance.submit', 'documents.file.view', 'ai.assistant.view', 'ai.assistant.create'] },
];
